import React, {useEffect, useState} from "react";
import {Link, useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import Button from "../../../../../components/Button";
import {useBusiness} from "../../../../../context/BusinessContext";
import {MarkdownField} from "../../../../../components/MarkdownField";
import SectionHeading from "../../../../../components/SectionHeading";
import {PencilIcon, SaveIcon} from "lucide-react";
import {TournamentDescriptionRequest, TournamentResponse} from "../../../../../types/tournament";

type OutletCtx = { tournamentId: string | null; isEdit?: boolean };

function isoToDisplay(iso?: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function ReadOnlyField({label, value}: { label: string; value?: string | null }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-900">{label}</label>
            <div className="mt-2 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {value && value.trim() ? value : "—"}
            </div>
        </div>
    );
}

export function DetailsStep(_props: { mode?: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const tournamentId = params.tournamentId ?? outlet.tournamentId ?? sp.get("tournamentId") ?? null;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [tournament, setTournament] = useState<TournamentResponse | null>(null);
    const [description, setDescription] = useState("");

    useEffect(() => {
        if (!businessId || !tournamentId) return;

        setLoading(true);
        (async () => {
            try {
                const {data} = await api.get(`/api/v2/business/${businessId}/tournaments/${tournamentId}`);
                const t = data as TournamentResponse;
                setTournament(t);
                setDescription(t?.description ?? "");
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load tournament details"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, tournamentId]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!businessId || !tournamentId) return;

        setSaving(true);
        try {
            const payload: TournamentDescriptionRequest = {description: description || null};
            await api.patch(
                `/api/v2/business/${businessId}/tournaments/${tournamentId}/description`,
                payload
            );
            setTournament((prev) => (prev ? {...prev, description: description || null} : prev));
            notify.success("Saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save tournament description"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading tournament…</div>
            ) : (
                <form onSubmit={onSubmit} className="space-y-6">
                    <SectionHeading
                        title="Tournament Details"
                        description="Tournament details are managed on the event. You can edit the description here."
                        actions={
                            <Button type="submit" variant="primary" size="md" disabled={saving}>
                                {saving ? "Saving…" :
                                    <span className="inline-flex items-center gap-2"><SaveIcon className="size-4"/>Save</span>}
                            </Button>
                        }
                    />

                    {/* Event-synced metadata (read-only) */}
                    <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
                        <div className="mb-5 flex items-start justify-between gap-4">
                            <div>
                                <p className="text-sm font-medium text-gray-900">Event details</p>
                                <p className="mt-0.5 text-xs text-gray-500">
                                    These are synced from the linked event and can't be edited here.
                                </p>
                            </div>
                            {tournament?.eventId && (
                                <Link
                                    to={`/business/events/${encodeURIComponent(tournament.eventId)}/edit/details`}
                                    className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                >
                                    <PencilIcon className="size-4"/>
                                    Edit event details
                                </Link>
                            )}
                        </div>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <ReadOnlyField label="Tournament name" value={tournament?.title}/>
                            <ReadOnlyField label="Default currency" value={tournament?.currency}/>
                            <ReadOnlyField label="Category" value={tournament?.category}/>
                            <ReadOnlyField label="Subcategory" value={tournament?.subCategory}/>
                            <ReadOnlyField label="Tournament start" value={isoToDisplay(tournament?.startTime)}/>
                            <ReadOnlyField label="Tournament end" value={isoToDisplay(tournament?.endTime)}/>
                            <ReadOnlyField label="Venue" value={tournament?.venue}/>
                            <ReadOnlyField label="Address" value={tournament?.address}/>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-900">Tags</label>
                                <div className="mt-2">
                                    {tournament?.tags?.length ? (
                                        <div className="flex flex-wrap gap-1.5">
                                            {tournament.tags.map((tag) => (
                                                <span key={tag}
                                                      className="inline-flex items-center rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-200">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">—</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Description (editable) */}
                    <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
                        <MarkdownField
                            title="Tournament description"
                            description="Describe your tournament. This will be shown to competitors, so make it engaging and informative!"
                            value={description}
                            onChange={(v) => setDescription(v)}
                        />
                    </div>
                </form>
            )}
        </div>
    );
}
